import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { FolderOpen, ChevronRight, Home } from 'lucide-react';
import { useCasoFolders, useMoveCaso } from '@/hooks/useCasoFolders';
import { CasoFolder } from '@/types/casos';

interface MoveCasoDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  casoId: string;
  currentFolderId?: string | null;
}

// Componente recursivo para tree view
function FolderTreeItem({ folder, level, onSelect, selectedId }: {
  folder: CasoFolder;
  level: number;
  onSelect: (folderId: string | null) => void;
  selectedId: string | null;
}) {
  const { data: subfolders = [] } = useCasoFolders(folder.id);
  const [expanded, setExpanded] = useState(false);

  return (
    <div>
      <Button
        variant={selectedId === folder.id ? "secondary" : "ghost"}
        className="w-full justify-start gap-2"
        style={{ paddingLeft: `${level * 1.5}rem` }}
        onClick={() => onSelect(folder.id)}
      >
        {subfolders.length > 0 && (
          <ChevronRight
            className={`h-4 w-4 transition-transform ${expanded ? 'rotate-90' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              setExpanded(!expanded);
            }}
          />
        )}
        <FolderOpen className="h-4 w-4" />
        <span className="truncate">{folder.name}</span>
      </Button>
      
      {expanded && subfolders.map(subfolder => (
        <FolderTreeItem
          key={subfolder.id}
          folder={subfolder}
          level={level + 1}
          onSelect={onSelect}
          selectedId={selectedId}
        />
      ))}
    </div>
  );
}

export function MoveCasoDialog({ open, onOpenChange, casoId, currentFolderId }: MoveCasoDialogProps) {
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const { data: rootFolders = [] } = useCasoFolders(null);
  const { mutate: moveCaso, isPending } = useMoveCaso();

  const handleMove = () => {
    moveCaso(
      { caso_id: casoId, folder_id: selectedFolderId },
      {
        onSuccess: () => {
          onOpenChange(false);
          setSelectedFolderId(null);
        },
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Mover Caso para Pasta</DialogTitle>
        </DialogHeader>

        <ScrollArea className="h-[400px] pr-4">
          <div className="space-y-1">
            {/* Opção raiz */}
            <Button
              variant={selectedFolderId === null ? "secondary" : "ghost"}
              className="w-full justify-start gap-2"
              onClick={() => setSelectedFolderId(null)}
            >
              <Home className="h-4 w-4" />
              <span>Raiz (sem pasta)</span>
            </Button>

            {/* Tree de pastas */}
            {rootFolders.map(folder => (
              <FolderTreeItem
                key={folder.id}
                folder={folder}
                level={0}
                onSelect={setSelectedFolderId}
                selectedId={selectedFolderId}
              />
            ))}
          </div>
        </ScrollArea>

        <div className="flex justify-end gap-2 pt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            onClick={handleMove}
            disabled={isPending || selectedFolderId === currentFolderId}
          >
            {isPending ? 'Movendo...' : 'Mover'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
